import React, { useState } from 'react';
import { XIcon } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
type ModalType = 'privacy' | 'terms' | null;
export function Footer() {
  const [activeModal, setActiveModal] = useState<ModalType>(null);
  return <>
      <footer className="py-12 px-6 bg-brown-900 border-t border-brown-800 text-center" data-id="element-37">
        <div className="max-w-3xl mx-auto" data-id="element-38">
          <p className="text-cream-100/60 font-serif italic text-lg mb-4" data-id="element-39">
            "Mi presencia irá contigo, y te daré descanso." — Éxodo 33:14
          </p>
          <p className="text-cream-100/40 text-sm mb-4" data-id="element-40">
            © {new Date().getFullYear()} El Escudo del Hogar Cristiano. Todos
            los derechos reservados.
          </p>
          <div className="flex items-center justify-center gap-4 text-sm" data-id="element-41">
            <button onClick={() => setActiveModal('privacy')} className="text-cream-100/50 hover:text-gold-400 transition-colors underline underline-offset-2" data-id="element-42">
              Política de Privacidad
            </button>
            <span className="text-cream-100/30" data-id="element-43">|</span>
            <button onClick={() => setActiveModal('terms')} className="text-cream-100/50 hover:text-gold-400 transition-colors underline underline-offset-2" data-id="element-44">
              Términos y Condiciones
            </button>
          </div>
        </div>
      </footer>

      <AnimatePresence data-id="element-45">
        {activeModal && <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.2
      }} className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm" onClick={() => setActiveModal(null)} data-id="element-46">
            <motion.div initial={{
          opacity: 0,
          scale: 0.95,
          y: 16
        }} animate={{
          opacity: 1,
          scale: 1,
          y: 0
        }} exit={{
          opacity: 0,
          scale: 0.95,
          y: 16
        }} transition={{
          duration: 0.25,
          ease: [0.21, 0.47, 0.32, 0.98]
        }} className="bg-cream-50 rounded-xl shadow-2xl max-w-2xl w-full max-h-[80vh] overflow-hidden" onClick={e => e.stopPropagation()} data-id="element-47">
              <div className="flex items-center justify-between px-6 py-4 border-b border-cream-200 bg-white" data-id="element-48">
                <h3 className="text-xl font-bold text-brown-900 font-serif" data-id="element-49">
                  {activeModal === 'privacy' ? 'Política de Privacidad' : 'Términos y Condiciones'}
                </h3>
                <button onClick={() => setActiveModal(null)} className="p-1 rounded-lg text-brown-800/60 hover:text-brown-900 hover:bg-cream-100 transition-colors" aria-label="Cerrar" data-id="element-50">
                  <XIcon className="w-5 h-5" data-id="element-51" />
                </button>
              </div>

              <div className="px-6 py-6 overflow-y-auto max-h-[calc(80vh-64px)] text-brown-800 leading-relaxed space-y-4" data-id="element-52">
                {activeModal === 'privacy' ? <PrivacyContent data-id="element-53" /> : <TermsContent data-id="element-54" />}
              </div>
            </motion.div>
          </motion.div>}
      </AnimatePresence>
    </>;
}
function PrivacyContent() {
  return <>
      <p className="text-sm text-brown-800/60" data-id="element-55">
        Última actualización: marzo 2026
      </p>

      <h4 className="text-lg font-bold font-serif text-brown-900" data-id="element-56">
        1. Información que Recopilamos
      </h4>
      <p data-id="element-57">
        Al adquirir nuestros productos digitales, recopilamos únicamente la
        información necesaria para procesar tu compra: nombre, dirección de
        correo electrónico y datos de pago. No almacenamos información de
        tarjetas de crédito directamente; estos datos son procesados de forma
        segura por nuestro proveedor de pagos.
      </p>

      <h4 className="text-lg font-bold font-serif text-brown-900" data-id="element-58">
        2. Uso de la Información
      </h4>
      <p data-id="element-59">Utilizamos tu información exclusivamente para:</p>
      <ul className="list-disc pl-6 space-y-1" data-id="element-60">
        <li data-id="element-61">Entregar el producto digital adquirido.</li>
        <li data-id="element-62">Enviar confirmaciones de compra y acceso al material.</li>
        <li data-id="element-63">
          Comunicarnos contigo en caso de actualizaciones relevantes del
          producto.
        </li>
      </ul>

      <h4 className="text-lg font-bold font-serif text-brown-900" data-id="element-64">
        3. Protección de Datos
      </h4>
      <p data-id="element-65">
        Implementamos medidas de seguridad razonables para proteger tu
        información personal contra acceso no autorizado, alteración o
        divulgación. Sin embargo, ninguna transmisión por Internet es
        completamente segura.
      </p>

      <h4 className="text-lg font-bold font-serif text-brown-900" data-id="element-66">
        4. Compartir Información
      </h4>
      <p data-id="element-67">
        No vendemos, intercambiamos ni transferimos tu información personal a
        terceros, excepto a proveedores de servicios necesarios para procesar tu
        compra (procesadores de pago, servicios de entrega de correo
        electrónico).
      </p>

      <h4 className="text-lg font-bold font-serif text-brown-900" data-id="element-68">
        5. Cookies
      </h4>
      <p data-id="element-69">
        Nuestro sitio puede utilizar cookies para mejorar tu experiencia de
        navegación. Puedes configurar tu navegador para rechazar cookies, aunque
        esto podría afectar algunas funcionalidades.
      </p>

      <h4 className="text-lg font-bold font-serif text-brown-900" data-id="element-70">
        6. Tus Derechos
      </h4>
      <p data-id="element-71">
        Tienes derecho a solicitar acceso, corrección o eliminación de tus datos
        personales en cualquier momento.
      </p>
    </>;
}
function TermsContent() {
  return <>
      <p className="text-sm text-brown-800/60" data-id="element-72">
        Última actualización: marzo 2026
      </p>

      <h4 className="text-lg font-bold font-serif text-brown-900" data-id="element-73">
        1. Producto Digital
      </h4>
      <p data-id="element-74">
        "El Escudo del Hogar Cristiano" es un producto digital (guía en formato
        descargable). Al completar tu compra, recibirás acceso inmediato al
        material. Este producto es para uso personal y no comercial.
      </p>

      <h4 className="text-lg font-bold font-serif text-brown-900" data-id="element-75">
        2. Precio y Pago
      </h4>
      <p data-id="element-76">
        El precio del producto es de $6.36 USD como ofrenda única. No hay cargos
        recurrentes ni suscripciones ocultas. El pago se procesa de forma segura
        a través de nuestro proveedor de pagos autorizado.
      </p>

      <h4 className="text-lg font-bold font-serif text-brown-900" data-id="element-77">
        3. Entrega
      </h4>
      <p data-id="element-78">
        Una vez confirmado el pago, recibirás acceso al producto digital de
        forma inmediata a través de un enlace de descarga enviado a tu correo
        electrónico.
      </p>

      <h4 className="text-lg font-bold font-serif text-brown-900" data-id="element-79">
        4. Política de Reembolso
      </h4>
      <p data-id="element-80">
        Debido a la naturaleza digital del producto, no ofrecemos reembolsos una
        vez que el material ha sido descargado o accedido. Si experimentas
        problemas técnicos para acceder al contenido, nos comprometemos a
        ayudarte a resolver la situación.
      </p>

      <h4 className="text-lg font-bold font-serif text-brown-900" data-id="element-81">
        5. Propiedad Intelectual
      </h4>
      <p data-id="element-82">
        Todo el contenido del producto está protegido por derechos de autor.
        Queda prohibida la reproducción, distribución o reventa del material sin
        autorización expresa por escrito.
      </p>

      <h4 className="text-lg font-bold font-serif text-brown-900" data-id="element-83">
        6. Descargo de Responsabilidad
      </h4>
      <p data-id="element-84">
        Este producto es una guía espiritual y devocional basada en principios
        bíblicos. No sustituye consejería profesional, terapia psicológica ni
        asesoría legal. Los resultados pueden variar según la dedicación y
        circunstancias individuales de cada persona.
      </p>

      <h4 className="text-lg font-bold font-serif text-brown-900" data-id="element-85">
        7. Aceptación
      </h4>
      <p data-id="element-86">
        Al realizar tu compra, confirmas que has leído y aceptas estos términos
        y condiciones en su totalidad.
      </p>
    </>;
}